import {
  Box, Card, CardContent, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableRow,
  Typography, MenuItem, Popover, Tooltip
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import React, { useEffect, useState } from "react";
import stylesheet from "../detail-container.stylesheet";
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DoubleArrowRoundedIcon from '@mui/icons-material/DoubleArrowRounded'
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined'
import HistoryPTComponent from "./history-pt.component";
import { useSelector } from "react-redux";
import { useDispatchAPIs } from "../../../../utils/use-dispatchApis-hook";
import moment from "moment";
import { RootState } from "../../../../redux/store";
import { checkCondition } from '../common/condition-check'
import NotesComponent from '../common/notes.component'
import Helper from "../../../../utils/helper";
import { formatDisplayName } from "../../../../utils/display-name-formatter";
import { useOverrideAction } from "../../../../utils/override-hook";
import ManualOverrideComponent from "../common/manual-override.component"
import { useCheckConfirmCount } from "../../../../utils/use-checkconfirmationcount-hook";
import RationaleDialogComponent from '../common/rationale-dialog.component';
import { formatOptionLabel } from '../../../../utils/display-name-formatter'

interface IProps {
  input: any;
  classes: ClassNameMap;
}

/**
 * Program-targets coloring rule: flag a Current value red when it has NOT met the
 * program's clinical target. This is *target attainment*, computed once by the backend
 * as `matches_target` (set_matches_target, data_collection.py) for EVERY target — so it
 * also covers ApoB, which carries a target_value but no lab reference range.
 *
 * Deliberately different from the Program Check panel, which flags by age/sex normal
 * (lab reference) ranges via value_range. Do NOT swap one rule for the other.
 *
 * Guards (no flag): value missing, or no real numeric target to compare against —
 * target_status 'lifestyle' (TLC) or 'unavailable' ('—'), where target_value is null
 * and matches_target is forced False by the backend.
 */
export const isOffTarget = (d: any): boolean => {
  if (d?.value === null || d?.value === undefined) return false;
  if (d?.target_status === 'lifestyle' || d?.target_status === 'unavailable') return false;
  if (d?.target_value === null || d?.target_value === undefined) return false;
  return d?.matches_target === false;
};

const ProgramTargetsComponent: React.FC<IProps> = ({ input, classes }) => {
  const [data, setData] = useState([] as any);
  const [currItem, setCurrItem] = useState({} as any);
  const [notesOpen, setNotesOpen] = useState(false)
  const [isAction, setIsAction] = useState('')
  const [syncStatus, setSyncStatus] = useState('')
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail);
  const { onSetOverView, onDataOverSubmit } = useOverrideAction()
  const [cardAnchorEl, setCardAnchorEl] = useState<null | HTMLElement>(null);
  const [targetAnchorEl, setTargetAnchorEl] = useState<null | HTMLElement>(null);
  const [openRationale, setOpenRationale] = useState(false)
  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => setCardAnchorEl(event.currentTarget);
  const handleCardClose = () => setCardAnchorEl(null);
  const handleTargetCardClick = (event: React.MouseEvent<HTMLElement>) => setTargetAnchorEl(event.currentTarget);
  const handleTargetCardClose = () => setTargetAnchorEl(null);
  let prgStatus = Helper.isPrgInActive(patientDetail?.program_enrollments, [input?.cProgram])
  const prgStatusColor = {
    fill: prgStatus ? 'rgba(0, 0, 0, 0.26) !important' : '',
    cursor: prgStatus ? 'default' : 'hand'
  }

  useEffect(() => {
    targetAnchorEl !== null && handleCardClose()
  }, [targetAnchorEl])

  useEffect(() => {
    handleCardClose()
  }, [isAction]);

  useEffect(() => {
    initialise(patientDetail?.measurements?.program_targets);
  }, [patientDetail]);


  const checkSyncStatus = (data: any) => {
    const _temp = data?.filter((f: { program: any; }) => f.program === input.cProgram) || []
    if (_temp?.length && _temp[0].last_sync_date) {
      setSyncStatus(`Sync ${_temp[0].sync_type} on ${moment.unix(_temp[0].last_sync_date).format("MMM DD, YYYY h:mm a")}`)
    }
  }

  const initialise = (data: any) => {
    const targetData = (input?.isActionItems === true)
      ? data?.filter((f: { program: any, needs_update: boolean }) => f.needs_update && f.program === input.actionProgram) || []
      : data?.filter((f: { program: any; }) => f.program === input.cProgram) || []

    setData({
      patientId: patientDetail?.id,
      program_targets: targetData,
      program_targets_mode: "view",
    });
    checkSyncStatus(data)
  };

  const enroll_id = patientDetail?.program_enrollments?.find((f: { program: any; }) => f.program === input.cProgram).enrollment_id

  const handleRationale = () => {
    handleTargetCardClose()
    setOpenRationale(true)
  }

  const handleRationaleClose = () => setOpenRationale(false)

  const onNotesClick = () => {
    setNotesOpen(!notesOpen)
    handleCardClose()
  }

  const onSetMode = (_mode: string, outputData: any[] = [], _syncStatus = '') => {
    setIsAction(_mode)
    if (_mode === 'history' && _syncStatus) {
      setSyncStatus(_syncStatus)
    } else if (_mode === 'override') {
      handleTargetCardClose()
      onSetOverView(currItem?.id, _mode, data)
    }
    setData({ ...data, program_targets_mode: _mode });
    setNotesOpen(false)
  };

  const onOverrideSubmit = (_id: any, date: number, is_overridden: boolean, overridenPrograms: any[]) => {
    handleCardClose()
    setIsAction('view')
  }

  const formatConfirmDate = (confirmed_on: number) => {
    return confirmed_on ? moment.unix(confirmed_on).format("MM/DD/YY") : ''
  };

  const getRowClass = (i: any) => (i + 1) === data?.program_targets.length ? classes.programTargetLastTableCell : classes.programTargetTableCell

  const rightHeaderCal = (d: any) => {
    return (
      <Typography variant="subtitle1" className={classes.cardValue} sx={{ color: isOffTarget(d) ? 'var(--color-error) !important' : 'inherit' }}>
        {checkCondition(d.value !== null, <strong>{formatOptionLabel(d.value)}{" "}</strong>, <> NA {" "}</>)}
      </Typography>
    );
  };

  const subHeaderCal = (d: any) => {
    if (d?.value_range?.min === null && d?.value_range?.max === null && d.unit === '')
      return <></>

    let unit = (!d?.unit || d.unit === 'NaN') ? '' : d.unit
    if (d?.value_range?.min === null && d?.value_range?.max === null && unit)
      return <Typography className={d?.is_overridden ? classes.overrideTitle : ''} color="textSecondary" variant="subtitle2">
        <small>&nbsp;({unit})</small>
      </Typography>

    let vrmin = (d?.value_range?.min !== null) ? d?.value_range?.min : ''
    let vrmax = (d?.value_range?.max !== null) ? d?.value_range?.max : ''
    let range = (vrmin || vrmax) ? (unit !== '') ? `${vrmin}-${vrmax} ` : `${vrmin}-${vrmax}` : ''
    return <Typography className={d?.is_overridden ? classes.overrideTitle : ''} color="textSecondary" variant="subtitle2">
      <small>&nbsp;({range}{unit || ''})</small>
    </Typography>
  }

  const renderTarget = () => {
    let targetData = input?.isActionItems === true
      ? data?.program_targets?.filter((i: { needs_update: any, is_overridden: any }) => i.is_overridden !== true && i.needs_update === true)
      : data?.program_targets

    if (input.cProgram === 'hypertension') {
      targetData = targetData?.sort((a: any, b: any) => (a.name < b.name ? 1 : -1));
    }

    return targetData?.map((d: any, i: number) => (
      <TableRow key={`target-${d?.display_name}-${i}`}
        className={d.is_overridden && d.needs_update
          ? classes.overrideTargetRow
          : d.needs_update
            ? classes.confirmCardBackground
            : classes.updatedTargetRow}
        onClick={(e) => {
          setCurrItem(d);
          handleTargetCardClick(e)
        }}>
        <TableCell className={getRowClass(i)} scope="column">
          <Grid container spacing={0} className={classes.programTargetName} alignItems={'center'}>
            <Grid item>
              {d.is_overridden
                ? <Tooltip title={`${formatDisplayName(d?.display_name)} overridden`}>
                  <Typography variant="body2" className={[classes.desc, classes.overrideTitle].join(' ')}>
                    {formatDisplayName(d?.display_name)}
                  </Typography>
                </Tooltip>
                : <Typography variant="body2" className={classes.desc} sx={{ color: d?.is_overridden ? 'var(--color-error) !important' : 'inherit' }}>
                  {formatDisplayName(d?.display_name)}
                </Typography>
              }
            </Grid>
            <Grid item className={classes.subHeaderTitle}>
              {subHeaderCal(d)}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell className={getRowClass(i)} align="right" width="10%">
          <Grid item>
            <Typography align="right" variant="subtitle2" color="textSecondary"
              className={[classes.subHeaderTitle, d?.is_overridden && classes.overrideTitle].join(' ')}
            >
              <small>{formatConfirmDate(d.date_recorded)}</small>
            </Typography>
          </Grid>
        </TableCell>
        <TableCell className={getRowClass(i)} align="right">
          <Grid item>
            <Typography
              variant="subtitle1"
              className={[classes.cardValue, d?.is_overridden && classes.overrideTitle].join(' ')}
              sx={{ color: (!d?.is_overridden && isOffTarget(d)) ? 'var(--color-error) !important' : 'inherit' }}
            >
              {d.value !== null ? <strong>{formatOptionLabel(d.value)}</strong> : <> NA {" "}</>}
            </Typography>
          </Grid>
        </TableCell>
        <TableCell className={getRowClass(i)} align="right">
          <Grid item>
            <Typography
              variant="subtitle1"
              className={[classes.cardValue, d?.is_overridden && classes.overrideTitle].join(' ')}
            >
              {d.target_status === 'lifestyle'
                ? <strong title="No pharmacologic target — therapeutic lifestyle changes">TLC</strong>
                : d.target_status === 'unavailable'
                  ? <span title="Target temporarily unavailable">—</span>
                  : <strong>{d.target_value}</strong>}
            </Typography>
          </Grid>
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <>
      {checkCondition(data?.program_targets?.length > 0,
        <Grid container spacing={0} direction="column" className={classes.customCard}>
          {((input?.isActionItems && data?.program_targets?.some((e: any) => e.needs_update && e.is_overridden !== true))
            || !input?.isActionItems) &&
            <Grid item className={classes.fullWidth}>
              <Card variant="outlined"
                className={checkCondition(data?.program_targets?.some((e: any) => (e.needs_update && e.is_overridden !== true) && e?.program === input?.cProgram),
                  classes.confirmCardBackground,
                  classes.compactTargetCard)}>
                <CardContent className={classes.programTargetContainer}>
                  <TableContainer onClick={handleCardClick}>
                    <Table className={classes.table} aria-label="simple table">
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={2} className={[classes.programTargetFirstTableCell, classes.programTargetFirstTableInputTitle].join(' ')} scope="row" >
                            <Typography variant="subtitle2" mb={0.3} className={classes.programTargetHeader}>
                              {input?.title}
                            </Typography>
                          </TableCell>
                          <TableCell className={[classes.programTargetFirstTableCell, classes.programTargetFirstTableWidth].join(' ')} align="right">
                            <Grid item>
                              <Typography color="textSecondary" mb={0.3} variant="subtitle2" className={classes.programTargetHeader}>
                                <small>&nbsp;Current</small>
                              </Typography>
                            </Grid>
                          </TableCell>
                          <TableCell className={[classes.programTargetFirstTableCell, classes.programTargetTargetHeader].join(' ')} align="right">
                            <Grid item>
                              <Typography color="textSecondary" mb={0.3} variant="subtitle2" className={classes.programTargetHeader}>
                                <small>Target</small>
                              </Typography>
                            </Grid>
                          </TableCell>
                        </TableRow>
                        {renderTarget()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {input?.isActionItems !== true && <>
                    <Popover
                      id={`target-item-${input?.cProgram}`}
                      open={Boolean(targetAnchorEl)}
                      anchorEl={targetAnchorEl}
                      onClose={handleTargetCardClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 3, horizontal: 'right' }}
                    >
                      <Box className={classes.compactMenuCard}>
                        {(currItem.needs_update || currItem.is_overridden) &&
                          <Tooltip title="Override">
                            <MenuItem
                              className={classes.compactTargetMenuItem}
                              onClick={() => !prgStatus && onSetMode('override')}
                            >
                              <DoubleArrowRoundedIcon sx={prgStatusColor} className={classes.overideIcon} />
                            </MenuItem>
                          </Tooltip>
                        }
                        <Tooltip title="Rationale">
                          <MenuItem className={classes.compactMenuItem} onClick={() => !prgStatus && handleRationale()}>
                            <PrivacyTipOutlinedIcon sx={prgStatusColor} className={classes.overideIcon} />
                          </MenuItem>
                        </Tooltip>
                        {/* <Tooltip title="Help">
                          <MenuItem className={classes.compactMenuItem} onClick={() => onHelpClick()}>
                            <HelpOutlineOutlinedIcon color="disabled" className={classes.compactMenuIcon} />
                          </MenuItem>
                        </Tooltip> */}
                      </Box>
                    </Popover>
                    <Popover
                      id={`target-menu-${input?.cProgram}`}
                      open={Boolean(cardAnchorEl)}
                      anchorEl={cardAnchorEl}
                      onClose={handleCardClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 4, horizontal: 40 }}
                    >
                      <Box className={classes.compactMenuCard}>
                        {syncStatus &&
                          <MenuItem sx={{ padding: '0.1rem 0.25rem 0.1rem 0.25rem' }}>
                            <Typography color="textSecondary" variant="subtitle2">
                              <small>{syncStatus}</small>
                            </Typography>
                          </MenuItem>
                        }
                        <MenuItem className={classes.compactTargetMenuItem}>
                          <IconButton
                            onClick={() => onSetMode("history")}
                            size='small'
                            className={classes.iconBaseBorder} >
                            <VisibilityOutlinedIcon className={classes.programTargetVisibilityIcon} />
                          </IconButton>
                        </MenuItem>
                        <MenuItem className={classes.compactTargetMenuItem}>
                          <IconButton
                            disabled={prgStatus}
                            size='small'
                            className={classes.iconBaseBorder}
                          >
                            <InsertDriveFileOutlinedIcon className={classes.editIcon} onClick={onNotesClick} />
                          </IconButton>
                        </MenuItem>
                        {/* <Tooltip title="Help">
                          <MenuItem className={classes.compactMenuItem} onClick={() => onHelpClick()}>
                            <HelpOutlineOutlinedIcon color="disabled" className={classes.compactMenuIcon} />
                          </MenuItem>
                        </Tooltip> */}
                      </Box>
                    </Popover>
                  </>
                  }
                </CardContent>
              </Card>
              {checkCondition(notesOpen,
                <Box mt={1}>
                  <NotesComponent
                    entityId={enroll_id}
                    entityRef={'enrollment'}
                    cancelBtn={true}
                    onCancel={onNotesClick}
                  />
                </Box>
              )}
              {currItem && data.program_targets_mode === 'override' && (
                <ManualOverrideComponent
                  entityRef='measurement'
                  input={currItem}
                  leftHeader={currItem?.name}
                  rightHeader={rightHeaderCal(currItem)}
                  subHeader={subHeaderCal(currItem)}
                  programs={[currItem?.program]}
                  closeOverride={() => {
                    handleCardClose()
                    onSetMode('view')
                  }}
                  onOverrideSubmit={onOverrideSubmit}
                />
              )}
            </Grid>
          }
        </Grid>
      )}
      {checkCondition(data.program_targets_mode === "history",
        <HistoryPTComponent input={input} name={"History"} outer={onSetMode} />
      )}
      {/* {openHelpDialog &&
        <ContextualHelpComponent
          open={openHelpDialog}
          handleClose={closeHelpDialog}
          id={currHelpItem?.id}
          title={currHelpItem?.title}
          programs={currItem?.program}
          category={'measurements'}
        />
      } */}
      {openRationale && (
        <RationaleDialogComponent
          open={openRationale}
          title={formatDisplayName(currItem?.display_name) || currItem?.name}
          category={'measurement'}
          input={{
            program: currItem?.program ? [currItem?.program] : [],
            name: currItem?.name
          }}
          handleClose={handleRationaleClose}
        />
      )}
    </>
  )
};

export default withStyles(stylesheet)(ProgramTargetsComponent);
